import React, { Fragment, useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';

interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  isDownloading?: boolean;
}

const Image: React.FC<Props> = ({
  src, height, width, isDownloading, ...rest
}) => {
  const [isImageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // When the image source changes, we need to reset the loaded state.
    // This ensures the skeleton shows for the new image.
    if (src) {
      setImageLoaded(false);
    }
  }, [src]);

  // If `isDownloading` is true, we show a skeleton and nothing else.
  // This is the primary loading state (e.g., fetching business data).
  if (isDownloading) {
    return (
      <Skeleton
        className="rounded-lg opacity-50"
        height={height}
        width={width}
      />
    );
  }

  return (
    <Fragment>
      {/* This skeleton shows while the image specified in `src` is loading */}
      {!isImageLoaded && (
        <Skeleton
          className="rounded-lg opacity-50"
          height={height}
          width={width}
        />
      )}
      <img
        src={src}
        onLoad={() => setImageLoaded(true)} // <-- Set the state when image is loaded
        style={{
          // Hide the image until it's fully loaded to prevent showing a broken image icon
          display: isImageLoaded ? 'block' : 'none',
          objectFit: 'contain',
          height: height || 'auto', // <-- Apply height prop
          width: width || '100%', // <-- Apply width prop
        }}
        {...rest}
      />
    </Fragment>
  );
};

export default Image;
